import React, { useEffect, useState } from "react";
import MegaLobby from "../components/MegaLobby";
import { getGames, peekGames } from "../lib/megaCache";
import { Search, X, CircleDot, Spade, Diamond, Layers, Dices, Tv, Zap, Rocket, Flame, Club, Crown, Sparkles } from "lucide-react";

// MegaNodes live-casino lobby (Evolution, Pragmatic Live, Microgaming, Spribe…).
// Category tabs filter the live catalogue by game title keywords.
const SPRIBE = 15; // Spribe mini games (Aviator, Mines, Plinko, Dice, Goal, Hi-Lo, Keno, Mini-Roulette, Hotline)
const CATS = [
  { key: "all", label: "All", icon: Layers, color: "text-[#f5b400]" },
  { key: "roulette", label: "Roulette", icon: CircleDot, color: "text-rose-400", re: /roulette|roleta|ruleta/i },
  { key: "andarbahar", label: "Andar Bahar", icon: Zap, color: "text-orange-400", re: /andar|bahar/i },
  { key: "teenpatti", label: "Teen Patti", icon: Club, color: "text-emerald-400", re: /teen ?patti|cards champion/i },
  { key: "blackjack", label: "Blackjack", icon: Spade, color: "text-sky-400", re: /blackjack|black ?jack/i },
  { key: "baccarat", label: "Baccarat", icon: Diamond, color: "text-fuchsia-400", re: /baccarat|punto banco|bac bo/i },
  { key: "dragon", label: "Dragon Tiger", icon: Flame, color: "text-red-400", re: /dragon tiger/i },
  { key: "poker", label: "Poker", icon: Crown, color: "text-amber-300", re: /poker|hold ?'?em|texas|side bet city/i },
  { key: "shows", label: "Game Shows", icon: Tv, color: "text-violet-400", re: /crazy|monopoly|dream|mega ball|wheel|funky|deal or|cash or|balloon|sweet bonanza|boom|lightning storm|spaceman|bingo|snakes|treasure|money time|ice fishing|powerball|gonzo|imperial quest|extra chilli|big bass|high flyer|crash|stock market|red door|mega fire|adventure|sugar|vegas ball|peek/i },
  { key: "dice", label: "Dice & Sic Bo", icon: Dices, color: "text-cyan-400", re: /sic bo|craps|dice|fan tan/i },
  { key: "more", label: "Lucky 7 & More", icon: Sparkles, color: "text-pink-400" },
  { key: "mini", label: "Mini Games", icon: Rocket, color: "text-lime-400", provider: SPRIBE },
  { key: "ezugi", label: "Ezugi", icon: Tv, color: "text-teal-300", provider: "hub" },
];

// Every live table lands in exactly ONE category: first keyword match wins, unmatched tables go to "Lucky 7 & More".
// Mini Games tab = Spribe + Aviatrix crash games; every other tab hides them so mini games never mix with live tables.
const RULES = CATS.filter((c) => c.re);
const isMini = (g) => g.provider_code === SPRIBE || g.provider_name === "Aviatrix";
const catOf = (g) => (RULES.find((c) => c.re.test(`${g.name || ""} ${g.category || ""}`)) || { key: "more" }).key;
const MATCHERS = Object.fromEntries(CATS.map((c) => [c.key, (g) => {
  if (c.key === "mini") return isMini(g);
  if (c.provider) return g.provider_code === c.provider;
  if (isMini(g)) return false;
  return c.key === "all" || catOf(g) === c.key;
}]));

export default function LiveCasino() {
  const [cat, setCat] = useState("all");
  const [hubGames, setHubGames] = useState(() => peekGames("Hub")?.games || []);
  useEffect(() => { getGames("Hub").then((d) => setHubGames(d.games)).catch(() => {}); }, []);
  const [q, setQ] = useState(null);

  return (
    <div className="max-w-3xl mx-auto bg-[#0b0b0b] min-h-[70vh] sm:px-0 pb-6" data-testid="live-casino-page">
      <div className="relative flex items-center justify-center px-3 py-3">
        <button onClick={() => setQ(q == null ? "" : null)} aria-label="Search" data-testid="live-casino-search-toggle"
          className="absolute left-3 grid place-items-center h-10 w-10 rounded-full border border-white/40 text-white">
          {q == null ? <Search className="h-5 w-5" /> : <X className="h-5 w-5" />}
        </button>
        <div className="flex flex-col items-center leading-tight" data-testid="live-casino-title">
          <h1 className="font-display font-extrabold text-[17px] inline-flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
              <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
            </span>
            <span className="deposit-gold-shine">Live Casino</span>
          </h1>
          <span className="text-[10px] text-white/90 font-medium tracking-wide">24/7 live dealer</span>
        </div>
      </div>
      {q != null && (
        <div className="px-3 pb-2">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search games…" data-testid="live-casino-search-input"
            className="w-full rounded-lg bg-[#1c1c1c] border border-[#333] px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#f5b400]" />
        </div>
      )}

      <div className="flex gap-5 overflow-x-auto no-scrollbar px-3 border-b border-white/10" data-testid="live-casino-tabs">
        {CATS.filter((c) => c.key !== "ezugi" || hubGames.length > 0).map((c) => (
          <button key={c.key} onClick={() => setCat(c.key)} data-testid={`live-casino-tab-${c.key}`}
            className={`relative shrink-0 py-2.5 text-[14px] font-semibold whitespace-nowrap transition-opacity ${c.color} ${cat === c.key ? "opacity-100 drop-shadow-[0_0_6px_currentColor]" : "opacity-60"}`}>
            {c.label}
            {cat === c.key && <span className="absolute left-1/2 -translate-x-1/2 bottom-0.5 h-1 w-1 rounded-full bg-[#f5b400]" />}
          </button>
        ))}
      </div>

      <div className="flex gap-3 overflow-x-auto no-scrollbar px-3 py-3" data-testid="live-casino-icons">
        {CATS.filter((c) => c.key !== "ezugi" || hubGames.length > 0).map((c) => {
          const Icon = c.icon;
          const active = cat === c.key;
          return (
            <button key={c.key} onClick={() => setCat(c.key)} aria-label={c.label} data-testid={`live-casino-icon-${c.key}`}
              className={`grid place-items-center h-14 w-14 shrink-0 rounded-full bg-[#1c1c1c] transition-[box-shadow] ${active ? "ring-2 ring-[#f5b400] shadow-[0_0_14px_rgba(245,180,0,0.5)]" : ""}`}>
              <Icon className={`h-6 w-6 ${c.color} ${active ? "" : "opacity-70"}`} />
            </button>
          );
        })}
      </div>

      <div className="space-y-3">
        <MegaLobby category="Live" query={q || ""} match={MATCHERS[cat]} extra={hubGames} hubFirst={cat === "roulette" || cat === "all" ? 30 : 0} />
      </div>
    </div>
  );
}
